import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GetModelRequest } from '@src/app/core/models/get-model.model';
import { InsertModelRequest } from '@src/app/core/models/insert-model.model';
import { SystemParameter } from '@src/app/features/web-submission/models/system-parameter.model';
import { Observable } from 'rxjs';
import { ApiBaseService, AppConfig } from 'sigv-library';
import { ApiResponse } from '../models/api-response.model';
import { CalculateAutoLifePremiumRequest } from '../models/calculate-auto-life-premium-request.model';
import { CalculateCarPremiumRequest } from '../models/calculate-car-premium-request.model';
import { CalculateInsuranceFeeRequest } from '../models/calculate-insurance-fee-request.model';
import { CalculateInsuranceFee } from '../models/calculate-insurance-fee.model';
import { CalculatePremium } from '../models/calculate-premium.model';
import { DropDownList, DropdownItem } from '../models/drop-down-list.model';
import { GetSalesDepartmentModelRequest } from '../models/get-sales-department-req.model';
import { GetSalesDepartmentModelRes } from '../models/get-sales-department-res.model';
import { SysParameter } from '../models/sys-parameter.model';
import { Product } from './../models/product.model';

@Injectable({
  providedIn: 'root',
})
export class ApiCommonService extends ApiBaseService {
  constructor(protected appConfig: AppConfig, protected http: HttpClient) {
    super(appConfig.getApiUrl('webSubmission'), http);
  }

  /**
   * @description 依據公司 id 取得所有產品資料
   * @param companyId 公司 id
   * @returns 產品資料
   */
  getAllProductsByCompanyId(
    companyId: string
  ): Observable<ApiResponse<Product[]>> {
    return this.doGet(`/Common/GetAllProduct/${companyId}`);
  }

  /**
   * @description 依據公司 id 取得所有產品資料
   * @param companyId 公司 id
   * @returns 產品資料
   */
  getAllProductsByCompanyIdNoConfirm(
    companyId: string
  ): Observable<ApiResponse<Product[]>> {
    return this.doGet(`/Common/GetAllProductNoConfirm/${companyId}`);
  }

  /**
   * @description 依據公司 id 取得產品資料
   * @param companyId 公司 id
   * @returns 產品資料
   */
  getProductsByCompanyId(
    companyId: string
  ): Observable<ApiResponse<Product[]>> {
    return this.doGet(`/Common/GetProduct/${companyId}`);
  }

  /**
   * @description 取得最新產品 By 進件
   * @param companyId 公司 id
   * @returns 產品資料
   */
  getProductByWebSubmission(
    companyId: string
  ): Observable<ApiResponse<Product[]>> {
    return this.doGet(`/Common/GetProductByWebSubmission/${companyId}`);
  }

  /**
   * @description 依據指定的 parent code 取得(連動)系統參數設定資料
   * @param parentCode 取得系統參數設定的 parent code
   * @returns 指定 parent code 的系統參數設定資料
   */
  getSystemParameterByParent(
    parentCode: string
  ): Observable<ApiResponse<SystemParameter[]>> {
    return this.doGet(`/Common/GetSystemParameterByParent/${parentCode}`);
  }

  /**
   * @description 依照指定的Code與paramType取得下拉資料
   */
  getDropdownList(
    code: string,
    paramType: string
  ): Observable<ApiResponse<DropDownList[]>> {
    return this.doPost('/Common/GetDropdownLists', {
      paramTypes: [{ code, paramType }],
    });
  }

  /**
   * @description 依據IdNo or CustomerName 模糊查詢客戶
   * @param customerNameOrIdNo id 號碼或客戶名稱
   * @returns 客戶資料
   */
  queryCustomer(
    customerNameOrIdNo: string
  ): Observable<ApiResponse<{ idNo: string; customerName: string }[]>> {
    const httpParam = new HttpParams().append('query', customerNameOrIdNo);
    return this.doGet('/Common/QueryCustomer', httpParam);
  }

  /**
   * @description 查詢多組系統參數
   * @param companyId 公司 id
   * @param types 系統參數 key 值，可一次查多種
   * @returns 多組系統參數
   */
  getManySysParameters(
    companyId: string,
    types: string[]
  ): Observable<ApiResponse<SysParameter[]>> {
    return this.doPost('/Common/GetManySysParameters', types, {
      'X-CompanyId': companyId,
    });
  }

  /**
   * @description 查詢 autolife 保費
   * @param requestPayload 查詢 autolife 保費參數
   * @returns autolife 保費資料
   */
  calculateAutoLifePremium(
    requestPayload: CalculateAutoLifePremiumRequest
  ): Observable<ApiResponse<CalculatePremium>> {
    return this.doPost('/Common/CalculateAutoLife', requestPayload, {
      'X-CompanyId': requestPayload.companyId.toString(),
    });
  }

  /**
   * @description 查詢 insurance 費率
   * @param requestPayload 查詢 autolife 保費參數
   * @returns autolife 保費資料
   */
  calculateInsuranceFee(
    requestPayload: CalculateInsuranceFeeRequest
  ): Observable<ApiResponse<CalculateInsuranceFee>> {
    return this.doPost('/Common/CalculateInsuranceFee', requestPayload, {
      'X-CompanyId': requestPayload.companyId.toString(),
    });
  }

  /**
   * @description 查詢車險保費
   * @param requestPayload 查詢車險保費參數
   * @returns 車險保費資料
   */
  calculateCarePremium(
    requestPayload: CalculateCarPremiumRequest
  ): Observable<ApiResponse<CalculatePremium>> {
    return this.doPost('/Common/CalculateAutoLife', requestPayload, {
      'X-CompanyId': requestPayload.companyId.toString(),
    });
  }

  /**
   * @description 新增Model - 擔保品車輛
   */
  insertModel(
    requestPayload: InsertModelRequest
  ): Observable<ApiResponse<void>> {
    return this.doPost('/Common/InsertModel', requestPayload, {
      'X-CompanyId': requestPayload.companyId.toString(),
    });
  }

  /**
   * @description 取得暫存的Model - 擔保品車輛
   * @returns 暫存的Model
   */
  getModel(
    requestPayload: GetModelRequest
  ): Observable<ApiResponse<DropdownItem[]>> {
    const httpParam = new HttpParams().append(
      'parentCode',
      requestPayload.parentCode
    );

    return this.doGet('/Common/GetModel', httpParam, {
      'X-CompanyId': requestPayload.companyId.toString(),
    });
  }

  /**
   * @description 查詢 Sales 資料權限
   * @returns departments
   */
  getSalesDepartment(
    requestPayload: GetSalesDepartmentModelRequest
  ): Observable<ApiResponse<GetSalesDepartmentModelRes>> {
    return this.doPost(
      '/Common/SearchJointPrivilegeFilterModel',
      requestPayload,
      {
        'X-CompanyId': requestPayload.companyId.toString(),
      }
    );
  }

  /**
   * @description 依地址代碼取得郵遞區號
   * @returns 郵遞區號列表
   */
  getPostcodesByAddressCode(
    addressCode: string
  ): Observable<ApiResponse<string[]>> {
    return this.doPost('/Common/GetPostcodesByAddressCode', { addressCode });
  }

  /**
   * @description 判斷是否超過列印次數/判斷密碼是否正確，若正確儲存列印次數及LOG
   * @param submissionNo Submission number
   * @param randomCode Random code (password)
   * @param userID User ID
   * @returns Status response
   */
  checkAndInsertAgreementPrintLog(
    submissionNo: number,
    randomCode: string,
    userID: string
  ): Observable<{ status: number }> {
    const httpParams = new HttpParams()
      .set('submissionNo', submissionNo.toString())
      .set('randomCode', randomCode)
      .set('userID', userID);

    return this.doGet<{ status: number }>(
      '/Common/CheckAndInsertAgreementPrintLog',
      httpParams
    );
  }

}
